import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AppFooter from 'components/AppFooter';
import AppHeader from 'components/AppHeader';
import {
  DashboardPage,
  MyAccountPage,
  ManagementPage,
  ResumePage,
  OpportunityCandidateCVPage,
  PlacementCandidateCVPage,
  PlacementsPage,
  AcceptTermsPage,
  MyNotificationSettings,
  MyDocumentsPage
} from 'components/pages';
import { MainSection } from 'components/styledComponents';
import PdfConfiguration from 'components/pages/ResumePage/sections/PersonalDetails/PdfConfiguration/PdfConfiguration';
import CompetenceProfile from 'components/pages/ResumePage/sections/CompetenceProfile/container';
import ExecutiveSummary from 'components/pages/ResumePage/sections/ExecutiveSummary/container';
import WorkExperience from 'components/pages/ResumePage/sections/WorkExperience/WorkExperience';
import Languages from 'components/pages/ResumePage/sections/Languages/Languages';
import ITSkills from 'components/pages/ResumePage/sections/ITSkills/ITSkills';
import OtherActivities from 'components/pages/ResumePage/sections/OtherActivities/container';
import Education from 'components/pages/ResumePage/sections/Education/Education';

import Eisblau1 from 'components/pages/ResumePage/images/EisblauT1.png';
import Eisblau2 from 'components/pages/ResumePage/images/EisblauT2.png';
import Eisblau3 from 'components/pages/ResumePage/images/EisblauT3.png';
import Grau1 from 'components/pages/ResumePage/images/GrauT1.png';
import Grau2 from 'components/pages/ResumePage/images/GrauT2.png';
import Grau3 from 'components/pages/ResumePage/images/GrauT3.png';
import Weiß1 from 'components/pages/ResumePage/images/WeibT1.png';
import Weiß2 from 'components/pages/ResumePage/images/WeibT2.png';
import Weiß3 from 'components/pages/ResumePage/images/WeibT3.png';
import Olivgrau1 from 'components/pages/ResumePage/images/OlivgrauT1.png';
import Olivgrau2 from 'components/pages/ResumePage/images/OlivgrauT2.png';
import Olivgrau3 from 'components/pages/ResumePage/images/OlivgrauT3.png';
import Hellgrau1 from 'components/pages/ResumePage/images/HellgrauT1.png';
import Hellgrau2 from 'components/pages/ResumePage/images/HellgrauT2.png';
import Hellgrau3 from 'components/pages/ResumePage/images/HellgrauT3.png';
import Rot1 from 'components/pages/ResumePage/images/RotT1.png';
import Rot2 from 'components/pages/ResumePage/images/RotT2.png';
import Rot3 from 'components/pages/ResumePage/images/RotT3.png';
import Blau1 from 'components/pages/ResumePage/images/BlauT1.png';
import Blau2 from 'components/pages/ResumePage/images/BlauT2.png';
import Blau3 from 'components/pages/ResumePage/images/BlauT3.png';

function PrivateContent({ onInit, showAgreement }) {
  const location = useLocation();
  const state = location.state;
  const background = state && state.background;

  useEffect(() => {
    onInit();
  }, [onInit]);

  if (typeof showAgreement === 'string') {
    showAgreement = JSON.parse(showAgreement);
  }

  const templates = useMemo(() => {
    return {
      '#c0cbd7': [Eisblau1, Eisblau2, Eisblau3],
      '#dddddd': [Grau1, Grau2, Grau3],
      '#ffffff': [Weiß1, Weiß2, Weiß3],
      '#9da7a2': [Olivgrau1, Olivgrau2, Olivgrau3],
      '#e6e9e6': [Hellgrau1, Hellgrau2, Hellgrau3],
      '#c31924': [Rot1, Rot2, Rot3],
      '#237ec5': [Blau1, Blau2, Blau3]
    };
  }, []);

  return (
    <React.Fragment>
      {!!showAgreement === false && <AppHeader />}
      <MainSection>
        {/* Main routes render with background for modal support */}
        <Routes location={background || location}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/management/*" element={<ManagementPage />} />
          <Route path="/agreement-terms" element={<AcceptTermsPage />} />
          <Route path="/account/*" element={<MyAccountPage />} />
          <Route path="/notification-settings" element={<MyNotificationSettings />} />
          <Route path="/resume/*" element={<ResumePage />} />

          <Route path="/documents" element={<MyDocumentsPage />} />
          <Route path="/placement/:placementId" element={<PlacementsPage />} />
          <Route
            path="/placement-resume/:placementId/:candidateId"
            element={<PlacementCandidateCVPage />}
          />
          <Route
            path="/opportunity-resume/:opportunityId/:candidateId"
            element={<OpportunityCandidateCVPage />}
          />
          <Route
            path="*"
            element={
              <Navigate
                to={{ pathname: '/dashboard', state: { fromLogin: state && state.fromLogin } }}
                replace
              />
            }
          />
        </Routes>

        {/* Modal routes render on top of ResumePage */}
        {background && (
          <Routes>
            <Route
              path="/resume/pdfcreation"
              element={<PdfConfiguration templates={templates} />}
            />
            <Route path="/resume/competence-profile" element={<CompetenceProfile />} />
            <Route path="/resume/executive-summary" element={<ExecutiveSummary />} />
            <Route path="/resume/languages" element={<Languages />} />
            <Route path="/resume/it-skills" element={<ITSkills />} />
            <Route path="/resume/other-activities" element={<OtherActivities />} />
            <Route path="/resume/education" element={<Education />} />
            <Route path="/resume/education/:id" element={<Education />} />

            <Route path="/resume/work-experience" element={<WorkExperience />} />
            <Route path="/resume/work-experience/:id" element={<WorkExperience />} />

            <Route path="/resume/add-company-group" element={<WorkExperience />} />
            <Route path="/resume/add-company-group/:id" element={<WorkExperience />} />

            <Route path="/resume/company" element={<WorkExperience />} />
            <Route path="/resume/company/:id" element={<WorkExperience />} />

            <Route path="/resume/position-in-company" element={<WorkExperience />} />
            <Route path="/resume/position-in-company/:id" element={<WorkExperience />} />
          </Routes>
        )}
      </MainSection>
      <AppFooter />
    </React.Fragment>
  );
}

PrivateContent.propTypes = {
  onInit: PropTypes.func.isRequired
};

export default PrivateContent;
